// src/shared/hooks/useDataAwareLookback.ts
//
// Lookback selector state that follows the ACTUAL price history available for
// the chosen tickers. Pairs a domain's coverage query with the shared
// `buildLookbackOptions` ladder and clamps the selection to the longest
// window the data can serve whenever the available options change (e.g. after
// adding a recently-listed ticker) -- the exact behaviour of the reference
// Drift Auditor form.

import { useEffect, useMemo, useState } from 'react';

import { buildLookbackOptions } from '../utils/lookback';
import type { LookbackCoverage, LookbackOption } from '../utils/lookback';

export interface DataAwareLookback {
    lookbackYears: number;
    setLookbackYears: (years: number) => void;
    lookbackOptions: LookbackOption[];
}

export function useDataAwareLookback(
    standardOptions: readonly number[],
    coverage: LookbackCoverage | null | undefined,
    initialYears: number,
): DataAwareLookback {
    const [lookbackYears, setLookbackYears] = useState<number>(initialYears);

    const lookbackOptions = useMemo(
        () => buildLookbackOptions(standardOptions, coverage),
        [standardOptions, coverage],
    );

    // Only clamp against REAL coverage. While coverage is loading / unknown the
    // options are the unfiltered standard ladder, and clamping to it would
    // clobber a re-hydrated "max available" window (e.g. 7Y) with 10Y.
    const hasCoverage = !!coverage && coverage.max_lookback_years > 0;
    useEffect(() => {
        if (!hasCoverage) return;
        const values = lookbackOptions.map((o) => o.value);
        if (values.length && !values.includes(lookbackYears)) {
            setLookbackYears(values[values.length - 1]);
        }
    }, [hasCoverage, lookbackOptions, lookbackYears]);

    return { lookbackYears, setLookbackYears, lookbackOptions };
}

export default useDataAwareLookback;
